import { Badge, Col, Row, Tabs, Radio, Empty, Tag, Spin, Button, message, Popover } from "antd";
import moment from "moment";
import { Link } from "react-router-dom";
import { format } from 'rut.js';
import Symbols from "./Symbols";
import { uniqBy } from 'lodash';
import { SyncOutlined } from '@ant-design/icons';
import { useTranslation } from "react-i18next";
import { timeDiff } from '../../../services/helper';
import { HasAccess } from "@permify/react-role";
import ExamTag from "./ExamTag";
import { getCookie } from '../../../services/cookies';
import { callPatient } from '../../../services/general';
import { useState } from "react";
import { useBoxContext } from '../../../context/BoxContext';
import groupedExams from '../../../utils/groupedExams.json';
import showOccupiedPatientModal from './OccupiedPatientModal';
import showBlockedCallModal from './BlockedCallModal';

const tabsConstants = {
  pending: {
    title: 'En Espera',
    states: ['created', 'done', 'occupied', 'fromJourney', 'fromDevice', 'inProgress'],
    empty: 'No hay examenes en espera'
  },
  reevaluation: {
    title: 'Reevaluaciones',
    states: ['created', 'done', 'reevaluate'],
    empty: 'No hay examenes en reevaluación'
  },
  treated: {
    title: 'Atendidos',
    states: ['done'],
    empty: 'No hay examenes completados'
  },
}

const ExamTabs = ({
  form,
  loading,
  tabsExams,
  currentPage,
  setCurrentPage,
  location,
  countryCode,
}) => {
  const states = ['created', 'reevaluate', 'occupied', 'inProgress', 'done'];
  const peruvianTabs = ['pending', 'reevaluation', 'treated'];
  const chileanTabs = ['pending', 'reevaluation', 'treated'];
  const formatDate = (value, template) => moment(value, 'YYYY-MM-DD').format(template);
  const { t } = useTranslation();

  const loadMore = (event, type) => {
    const direction = event.target.value;
    setCurrentPage(page => ({ ...page, [type]: direction === 'next' ? page[type] + 1 : page[type] - 1 }));
  };

  const { hasBoxes } = useBoxContext();
  const [loadingPerRow, setLoadingPerRow] = useState({});

  const handleCallPatient = async (admissionPatientId, forceOccupiedCall = false, forceRecentCall = false) => {
    const box = getCookie('box_id');
    if (!box || typeof box !== 'string' || box.trim() === '') {
      message.warning(t('examPage.examTabs.callPatientBoxWarning'));
      return;
    }

    setLoadingPerRow(prev => ({ ...prev, [admissionPatientId]: true }));

    try {
      await callPatient({ admissionPatientId, box, forceOccupiedCall, forceRecentCall });
      message.success(t('examPage.examTabs.callPatientSuccess'));
    } catch (error) {
      const errorMessage = typeof error === 'string' ? error : error?.message || error?.toString?.() || 'Error';

      if (errorMessage?.includes('PATIENT_OCCUPIED_IN_EXAM') && !forceOccupiedCall) {
        setLoadingPerRow((prev) => ({ ...prev, [admissionPatientId]: false }));
        showOccupiedPatientModal(errorMessage, () => handleCallPatient(admissionPatientId, true, false), t);
        return;
      }

      if (errorMessage?.includes('PATIENT_RECENTLY_CALLED') && !forceRecentCall) {
        setLoadingPerRow((prev) => ({ ...prev, [admissionPatientId]: false }));
        showBlockedCallModal(errorMessage, () => handleCallPatient(admissionPatientId, forceOccupiedCall, true), t);
        return;
      }

      message.error(errorMessage || t('examPage.examTabs.callPatientError'));
      console.error('Error callPatient', error);
    } finally {
      setLoadingPerRow(prev => ({ ...prev, [admissionPatientId]: false }));
    }
  };

  const customTableHeader = (tabTypeData, tabType) => {
    return (
      <Row className="cmt-custom-table-header">
        <Col
          lg={3}
          xs={12}
          className="align-item-center-start p-10"
        >
          <h3>{t('examPage.examTabs.patient')}</h3>
        </Col>
        <Col
          lg={3}
          xs={12}
          className="center-item p-10"
        >
          <h3 className="text-center">
            {t('examPage.examTabs.documentId')}
          </h3>
        </Col>
        <Col
          lg={2}
          xs={24}
          className="center-item p-10"
        >
          <h3>Diabético</h3>
        </Col>
        <Col
          lg={4}
          xs={24}
          className="center-item p-10"
        >
          <h3>{t('examPage.examTabs.healthServices')}</h3>
        </Col>
        <Col
          lg={4}
          xs={24}
          className="center-item p-10"
        >
          <h3>{t('examPage.examTabs.psychoServices')}</h3>
        </Col>
        <Col
          lg={3}
          xs={12}
          className="center-item p-10"
        >
          <h3 className="text-center">
            {t('examPage.examTabs.medicalConsultation')}
          </h3>
        </Col>
        <Col
          lg={3}
          xs={12}
          className="center-item p-10"
        >
          <h3 className="text-center">Fecha</h3>
        </Col>
        <Col
          lg={2}
          xs={24}
          className="center-item p-10"
        >
          <h3 className="text-center">
            {['pending', 'occupied'].includes(tabType) ? 'Tiempo en sistema' : 'Fecha de exámenes completados'}
          </h3>
        </Col>
      </Row>
    );
  };


  const checkDoctorServices = (services, examName = 'Medicina') => {
    const doctorServices = services.filter(service => service.to_doctor)
    const isCreated = doctorServices.every(service => service.admission_patient_exam_state === 'created');
    const isDone = doctorServices.every(service => service.admission_patient_exam_state === 'done');
    return {
      ...doctorServices[0],
      exam_name: examName,
      admission_patient_exam_state: isCreated ? 'created' : (isDone ? 'done' : 'occupied'),
    }
  }

  const customTableBody = (tabTypeData, tabType, inx) => {
    const checkHealthServices = () => {
      if (tabTypeData.health_exams && tabTypeData.health_exams.length > 0) {
        const services = tabTypeData.health_exams.filter(
          (service) => !Object.keys(groupedExams).includes(service.exam_type) && !service.to_doctor
        );

        Object.keys(groupedExams).forEach(groupType => {
          const groupExams = tabTypeData.health_exams.filter(
            (service) => service.exam_type === groupType
          );

          if (groupExams.length > 0) {
            const servicesStates = uniqBy(groupExams, 'admission_patient_exam_state');
            const stateResult = states.find((state) =>
              servicesStates.find((service) => service.admission_patient_exam_state === state)
            );
            const representativeService = servicesStates.find(
              (service) => service.admission_patient_exam_state === stateResult
            );

            if (representativeService) {
              const groupedService = {
                ...representativeService,
                exam_name: groupedExams[groupType],
                exam_type: groupType,
              };

              const insertIndex = services.findIndex(service =>
                service.exam_name.localeCompare(groupedExams[groupType]) > 0
              );

              if (insertIndex === -1) {
                services.push(groupedService);
              } else {
                services.splice(insertIndex, 0, groupedService);
              }
            }
          }
        });

        if (tabTypeData.health_exams?.some(service => service.to_doctor)) {
          const toDoctor = checkDoctorServices(tabTypeData.health_exams);
          if (!services.some(service => service.to_doctor))
            services.push(toDoctor);
        }

        return (
          <Row>
            {
              services.map((exam, index) =>
                exam &&
                <Col key={`${exam.exam_id}-${index}`}>
                  <HasAccess
                    roles={['super_usuario', 'equipo_medico', 'equipo_psicologia', 'branch_office_manager', 'equipo_medico_externo']}
                    permissions='exam-detail'
                    renderAuthFailed={
                      <ExamTag exam={exam} className="mb-5" />
                    }
                  >
                    {
                      exam.to_doctor ?
                        <ExamTag exam={exam} className="mb-5" />
                        :
                        <Link
                          to={{
                            pathname: `/examenes/${Object.keys(groupedExams).includes(exam.exam_type) ? exam.admission_patient_id : exam.admission_patient_exam_id}`,
                            search: Object.keys(groupedExams).includes(exam.exam_type) ? `?groupType=${exam.exam_type}` : '',
                            state: {
                              search: location.search,
                              admissionPatientId: exam.admission_patient_id,
                            },
                          }}
                          key={`${exam.exam_id}-${index}`}
                          className="mb-5"
                        >
                          <ExamTag exam={exam} className="mb-5" />
                        </Link>
                    }
                  </HasAccess>
                </Col>
              )
            }
          </Row>
        );
      }

      return <p className='mb-0 text-center'>-</p>;
    };


    const checkPsychologyServices = () => {
      if (tabTypeData.psychology_exams) {
        const services = tabTypeData.psychology_exams?.filter(service => !service.to_doctor)
        if (tabTypeData.psychology_exams?.some(service => service.to_doctor)) {
          const toDoctor = checkDoctorServices(
            tabTypeData.psychology_exams,
            t('examPage.examTabs.psychologyDoctorExam'),
          );
          if (!services.some(service => service.to_doctor))
            services.push(toDoctor);
        }
        return (
          <Row>
            {services?.map((exam, index) => {
              if (exam) {
                return (
                  <Col key={`${exam.exam_id}-${index}`}>
                    <HasAccess
                      roles={['super_usuario', 'equipo_medico', 'equipo_psicologia',
                        'branch_office_manager', 'equipo_medico_externo']}
                      permissions='exam-detail'
                      renderAuthFailed={
                        <ExamTag
                          exam={exam}
                          className="mb-5"
                        />
                      }
                    >
                      {
                        exam.to_doctor ?
                          <ExamTag
                            exam={exam}
                            className="mb-5"
                          />
                          :
                          <Link
                            to={{
                              pathname: `/examenes/${exam.admission_patient_exam_id}`,
                              state: { from: location.pathname, search: location.search },
                            }}
                            className={`mb-5 ${exam.admission_patient_exam_state}`}
                          >
                            <ExamTag
                              exam={exam}
                              className="mb-5"
                            />
                          </Link>
                      }
                    </HasAccess>
                  </Col>
                );
              }
            })}
          </Row>
        );
      }

      return <p className='mb-0 text-center'>-</p>;
    };

    const isToday = moment(tabTypeData.admission_date, 'YYYY-MM-DD').isSame(moment(), 'day');

    return (
      <Row className="table-body mb-15" key={inx}>
        <Col
          lg={3}
          xs={12}
          className="align-item-center p-10"
        >
          <h3 className="hide-desktop text-center">{t('examPage.examTabs.patient')}</h3>
          <p className="mb-0">
            {tabTypeData.full_name}
            {form?.getFieldValue("allBranchOffices") && (
              <Tag color="processing">{tabTypeData.branch_office_name}</Tag>
            )}
            {
              (tabType === 'treated' && tabTypeData.revaluation) &&
              <SyncOutlined
                className='revaluation-icon'
              />
            }
            <HasAccess
              roles={['super_usuario', 'equipo_medico', 'equipo_psicologia', 'doctor', 'branch_office_manager']}
              permission="call-patient"
            >
              {hasBoxes && (
                <div className="mt-5">
                  <Popover
                    content={!isToday ? t('examPage.examTabs.callPatientDisabledTooltip') : ''}
                    placement="top"
                  >
                    <span>
                      {(() => {
                        const admissionId =
                          (Array.isArray(tabTypeData.health_exams) && tabTypeData.health_exams.length > 0)
                            ? tabTypeData.health_exams[0].admission_patient_id
                            : (Array.isArray(tabTypeData.psychology_exams) && tabTypeData.psychology_exams.length > 0)
                              ? tabTypeData.psychology_exams[0].admission_patient_id
                              : null;

                        return (
                          admissionId && (
                            <Button
                              loading={loadingPerRow[admissionId]}
                              disabled={!isToday || loadingPerRow[admissionId]}
                              onClick={() => handleCallPatient(admissionId)}
                              className="cmt-button secondary mt-5 call-patient-button"
                              type="primary"
                              size="small"
                            >
                              {t('examPage.examTabs.callPatient')}
                            </Button>
                          )
                        );
                      })()}
                    </span>
                  </Popover>
                </div>
              )}
            </HasAccess>
          </p>
        </Col>
        <Col
          lg={3}
          xs={12}
          className="center-item p-10"
        >
          <h3 className="hide-desktop text-center">
            {t('examPage.examTabs.documentId')}
          </h3>
          <p className='mb-0 text-center'>
            {tabTypeData.document_type === 'rut' ? format(tabTypeData.document_number) : tabTypeData.document_number}
          </p>
        </Col>
        <Col
          lg={2}
          xs={24}
          className="center-item p-10"

        >
          <h3 className="hide-desktop text-center">Diabético</h3>
          <p className="mb-0">{tabTypeData.diabetic ? 'SI' : 'NO'}</p>
        </Col>
        <Col
          lg={4}
          xs={24}
          className={`p-10 ${(!tabTypeData.health_exams) ? 'center-item' : 'align-item-center'}`}
        >
          <h3 className="hide-desktop text-center">
            {t('examPage.examTabs.healthServices')}
          </h3>
          {checkHealthServices()}
        </Col>
        <Col
          lg={4}
          xs={24}
          className={`p-10 ${(!tabTypeData.psychology_exams) ? 'center-item' : 'align-item-center'}`}
        >
          <h3 className="hide-desktop text-center">
            {t('examPage.examTabs.psychoServices')}
          </h3>
          {checkPsychologyServices()}
        </Col>
        <Col
          lg={3}
          xs={12}
          className="center-item p-10"
        >
          <h3 className="hide-desktop text-center">
            {t('examPage.examTabs.medicalConsultation')}
          </h3>
          <p className="mb-0 text-center">
            {tabTypeData.medical_consultation_state === 'pending' ? 'Pendiente' : 'Finalizado'}
          </p>
        </Col>
        <Col
          lg={3}
          xs={12}
          className="center-item p-10"
        >
          <h3 className="hide-desktop text-center">Fecha</h3>
          <p className=" mb-0 text-center">
            {formatDate(tabTypeData.admission_date, 'DD-MM-YYYY')}
          </p>
        </Col>
        <Col
          lg={2}
          xs={24}
          className="center-item p-10"
        >
          <h3 className="hide-desktop text-center">
            {['pending', 'occupied'].includes(tabType) ? 'Tiempo en sistema' : 'Fecha de exámenes completados'}
          </h3>
          <p className="mb-0 text-center">
            {
              ['pending', 'occupied'].includes(tabType)
                ? timeDiff(tabTypeData.created_at, undefined, tabTypeData.timezone, true)
                : `${formatDate(tabTypeData.date_done, 'DD/MM/YYYY')}\n${moment(tabTypeData.date_done).format('HH:mm')}`
            }
          </p>
        </Col>
      </Row>
    );
  };



  return (
    <Tabs className="cmt-tabs" defaultActiveKey="pending">
      {
        (countryCode === 'PE'
          ? peruvianTabs
          : chileanTabs).map(tabType =>
            <Tabs.TabPane
              key={tabType}
              tab={
                <Badge
                  showZero
                  offset={[15, 0]}
                  count={tabsExams[tabType].pagination.total || 0}
                >
                  <p className="mb-0">{tabsConstants[tabType].title}</p>
                </Badge>
              }
            >
              <Row justify='center'>
                <Col lg={22} xs={22}>
                  <Spin className="cmt-spin" spinning={loading[tabType]}>
                    {
                      tabsExams[tabType].pagination.to ?
                        <Row gutter={[0, 20]}>
                          <Col lg={24} xs={24}>
                            <Symbols types={tabsConstants[tabType].states} />
                          </Col>
                          <Col lg={24} xs={24}>
                            <Row>
                              <Col lg={24} xs={24}>
                                {customTableHeader(tabsExams[tabType].data, tabType)}
                              </Col>
                              <Col lg={24} xs={24}>
                                {
                                  tabsExams[tabType].data.map((elem, inx) => {
                                    return (
                                      <>
                                        {customTableBody(elem, tabType, inx)}
                                      </>
                                    );
                                  })
                                }
                              </Col>
                            </Row>
                          </Col>
                          <Col lg={24} xs={24}>
                            <Row className='mt-30 mb-40'>
                              <Col>
                                <Radio.Group className='cmt-pagination'>
                                  <Radio.Button
                                    value="prev"
                                    disabled={currentPage[tabType] === 1}
                                    onClick={(event) => loadMore(event, tabType)}
                                  >
                                    Anterior
                                  </Radio.Button>
                                  <Radio.Button
                                    value="next"
                                    disabled={tabsExams[tabType].pagination.lastPage === currentPage[tabType]}
                                    onClick={(event) => loadMore(event, tabType)}
                                  >
                                    Siguiente
                                  </Radio.Button>
                                </Radio.Group>
                              </Col>
                            </Row>
                          </Col>
                        </Row>
                        :
                        <Empty
                          image={Empty.PRESENTED_IMAGE_SIMPLE}
                          description={
                            <p className='text-center'>{tabsConstants[tabType].empty}</p>
                          }
                        />
                    }
                  </Spin>
                </Col>
              </Row>
            </Tabs.TabPane>
          )
      }
    </Tabs>
  )
}

export default ExamTabs;